import type { CartServiceItem } from "@/lib/types/cart.types"
import type { GroupAvailabilityResponse } from "@/lib/types/appointment.types"
import type { PaymentMethodsConfig } from "@/lib/types/settings.types"
import type { RootStore } from "./root-store"
import { makeAutoObservable } from "mobx"
import {
  DEFAULT_BUSINESS_TIMEZONE,
  DEFAULT_MAX_AVAILABLE_SLOTS_DAYS,
} from "@/lib/constants/timezones"

export type SelectedSlot = { startTime: string }
export type PaymentMethod = "Cash" | "Transfer" | "Card"

/** Which booking flow the customer picked on the entry screen. */
export type BookingMode = "single" | "group"

/**
 * Steps are addressed by name because the group flow has an extra guests step
 * ahead of the shared services/time/confirm sequence.
 */
export type BookingStepId = "guests" | "services" | "time" | "confirm"

const SINGLE_STEPS: BookingStepId[] = ["services", "time", "confirm"]
const GROUP_STEPS: BookingStepId[] = ["guests", "services", "time", "confirm"]

export const BOOKING_STEP_LABELS: Record<BookingStepId, string> = {
  guests: "Guests",
  services: "Services",
  time: "Time",
  confirm: "Confirm",
}

let groupMemberSeq = 0

/** A locally-edited beneficiary/seat before the group is submitted. */
export type GroupMemberDraft = {
  localId: string
  beneficiaryName: string
  beneficiaryNotes: string
  beneficiaryPhone: string
  beneficiaryEmail: string
  services: CartServiceItem[]
  startTime: string
  isOrganizerSeat: boolean
}

export function createEmptyGroupMember(
  overrides?: Partial<GroupMemberDraft>,
): GroupMemberDraft {
  groupMemberSeq += 1
  return {
    localId: `gm_${Date.now()}_${groupMemberSeq}`,
    beneficiaryName: "",
    beneficiaryNotes: "",
    beneficiaryPhone: "",
    beneficiaryEmail: "",
    services: [],
    startTime: "",
    isOrganizerSeat: false,
    ...overrides,
  }
}

export class AppointmentStore {
  rootStore: RootStore
  selectedServices: CartServiceItem[] = []
  currentStep: number = 1
  selectedDate: string = ""
  selectedSlot: SelectedSlot | null = null
  selectedPaymentMethod: PaymentMethod = "Cash"
  isLoading: boolean = false
  isCustomBooking: boolean = false
  acceptedTerms: boolean = false
  notificationsConsent: boolean = true
  bookingNotes: string = ""
  isBookingHydrating: boolean = false
  businessTimezone: string = DEFAULT_BUSINESS_TIMEZONE
  maxAvailableSlotsDays: number = DEFAULT_MAX_AVAILABLE_SLOTS_DAYS
  maxGroupBookingSize: number = 6
  paymentMethodsConfig: PaymentMethodsConfig | null = null
  discountCode: string = ""

  /** `null` while the customer is still on the "how do you want to book" screen. */
  bookingMode: BookingMode | null = null

  // Group booking state
  groupDate: string = ""
  groupMembers: GroupMemberDraft[] = []
  activeGroupMemberId: string | null = null
  groupPaymentMethod: PaymentMethod = "Cash"
  groupIsCustomBooking: boolean = false
  groupNotes: string = ""
  groupAvailability: GroupAvailabilityResponse | null = null

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore

    makeAutoObservable(this, {
      rootStore: false,
    })
  }

  // ---- Mode & steps ----
  get isGroupMode(): boolean {
    return this.bookingMode === "group"
  }

  get steps(): BookingStepId[] {
    return this.isGroupMode ? GROUP_STEPS : SINGLE_STEPS
  }

  get totalSteps(): number {
    return this.steps.length
  }

  get currentStepId(): BookingStepId {
    return this.steps[this.currentStep - 1] ?? this.steps[0]
  }

  setBookingMode(mode: BookingMode | null) {
    if (this.bookingMode === mode) return
    this.bookingMode = mode
    this.currentStep = 1
    if (mode === "group" && this.groupMembers.length === 0) {
      this.addGroupMember()
    }
  }

  previousStep() {
    if (this.currentStep > 1) {
      this.currentStep--
    }
  }

  nextStep() {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++
    }
  }

  setCurrentStep(step: number, force = false) {
    if (step < 1 || step > this.totalSteps) return
    // Only allow navigating to current or prior steps unless forced (e.g. via URL hydration)
    if (!force && step > this.currentStep) return
    this.currentStep = step
  }

  setIsLoading(value: boolean) {
    this.isLoading = value
  }

  setSelectedServices(services: CartServiceItem[]) {
    this.selectedServices = services
  }

  setSelectedDate(date: string) {
    this.selectedDate = date
  }

  setSelectedSlot(slot: SelectedSlot | null) {
    this.selectedSlot = slot
  }

  setSelectedPaymentMethod(method: PaymentMethod) {
    this.selectedPaymentMethod = method
  }

  setIsCustomBooking(value: boolean) {
    this.isCustomBooking = value
    if (value) {
      this.clearDateTime()
    }
  }

  setAcceptedTerms(value: boolean) {
    this.acceptedTerms = value
  }

  setNotificationsConsent(value: boolean) {
    this.notificationsConsent = value
  }

  setBookingNotes(value: string) {
    this.bookingNotes = value
  }

  initNotificationsConsentFromUser(value?: boolean) {
    this.notificationsConsent = value ?? true
  }

  setIsBookingHydrating(value: boolean) {
    this.isBookingHydrating = value
  }

  setBookingSettings(
    timezone: string,
    maxAvailableSlotsDays: number,
    maxGroupBookingSize?: number,
  ) {
    this.businessTimezone = timezone
    this.maxAvailableSlotsDays = maxAvailableSlotsDays
    if (maxGroupBookingSize && maxGroupBookingSize >= 2) {
      this.maxGroupBookingSize = maxGroupBookingSize
    }
  }

  setPaymentMethodsConfig(config: PaymentMethodsConfig) {
    this.paymentMethodsConfig = config
  }

  setDiscountCode(code: string) {
    this.discountCode = code
  }

  // ---- Group booking ----
  get activeMember(): GroupMemberDraft | null {
    if (!this.isGroupMode) return null
    return (
      this.groupMembers.find((m) => m.localId === this.activeGroupMemberId) ??
      this.groupMembers[0] ??
      null
    )
  }

  setActiveGroupMember(localId: string) {
    this.activeGroupMemberId = localId
  }

  setGroupDate(date: string) {
    this.groupDate = date
    this.groupAvailability = null
  }

  setGroupPaymentMethod(method: PaymentMethod) {
    this.groupPaymentMethod = method
  }

  setGroupIsCustomBooking(value: boolean) {
    this.groupIsCustomBooking = value
    this.groupAvailability = null
    if (value) {
      this.groupDate = ""
      this.groupMembers = this.groupMembers.map((m) => ({ ...m, startTime: "" }))
    }
  }

  setGroupNotes(value: string) {
    this.groupNotes = value
  }

  setGroupAvailability(availability: GroupAvailabilityResponse | null) {
    this.groupAvailability = availability
  }

  addGroupMember(member?: GroupMemberDraft) {
    if (this.groupMembers.length >= this.maxGroupBookingSize) return
    const next = member ?? createEmptyGroupMember()
    this.groupMembers = [...this.groupMembers, next]
    this.activeGroupMemberId = next.localId
    this.groupAvailability = null
  }

  removeGroupMember(localId: string) {
    this.groupMembers = this.groupMembers.filter((m) => m.localId !== localId)
    if (this.activeGroupMemberId === localId) {
      this.activeGroupMemberId = this.groupMembers[0]?.localId ?? null
    }
    this.groupAvailability = null
  }

  updateGroupMember(localId: string, patch: Partial<GroupMemberDraft>) {
    this.groupMembers = this.groupMembers.map((m) =>
      m.localId === localId ? { ...m, ...patch } : m,
    )
  }

  /** Applies one start time to every seat, e.g. from a same-time suggestion. */
  setAllGroupStartTimes(startTime: string) {
    this.groupMembers = this.groupMembers.map((m) => ({ ...m, startTime }))
    this.groupAvailability = null
  }

  get groupTotalPrice(): number {
    return this.groupMembers.reduce(
      (sum, m) => sum + m.services.reduce((s, svc) => s + (Number(svc.price) || 0), 0),
      0,
    )
  }

  get groupTotalDuration(): number {
    return this.groupMembers.reduce(
      (sum, m) => sum + m.services.reduce((s, svc) => s + (Number(svc.duration) || 0), 0),
      0,
    )
  }

  /**
   * The single/group agnostic view of the booking currently being edited.
   * Shared step components read and write through these so they stay identical
   * across both flows.
   */
  get activeServices(): CartServiceItem[] {
    if (!this.isGroupMode) return this.selectedServices
    return this.activeMember?.services ?? []
  }

  get activeDate(): string {
    return this.isGroupMode ? this.groupDate : this.selectedDate
  }

  get activeStartTime(): string {
    if (!this.isGroupMode) return this.selectedSlot?.startTime ?? ""
    return this.activeMember?.startTime ?? ""
  }

  get activeIsCustomBooking(): boolean {
    return this.isGroupMode ? this.groupIsCustomBooking : this.isCustomBooking
  }

  get activePaymentMethod(): PaymentMethod {
    return this.isGroupMode ? this.groupPaymentMethod : this.selectedPaymentMethod
  }

  get activeNotes(): string {
    return this.isGroupMode ? this.groupNotes : this.bookingNotes
  }

  setActiveServices(services: CartServiceItem[]) {
    if (!this.isGroupMode) {
      this.setSelectedServices(services)
      return
    }
    const member = this.activeMember
    if (!member) return
    this.updateGroupMember(member.localId, { services })
    this.groupAvailability = null
  }

  setActiveDate(date: string) {
    if (this.isGroupMode) this.setGroupDate(date)
    else this.setSelectedDate(date)
  }

  setActiveStartTime(startTime: string | null) {
    if (!this.isGroupMode) {
      this.setSelectedSlot(startTime ? { startTime } : null)
      return
    }
    const member = this.activeMember
    if (!member) return
    this.updateGroupMember(member.localId, { startTime: startTime ?? "" })
    this.groupAvailability = null
  }

  setActiveIsCustomBooking(value: boolean) {
    if (this.isGroupMode) this.setGroupIsCustomBooking(value)
    else this.setIsCustomBooking(value)
  }

  setActivePaymentMethod(method: PaymentMethod) {
    if (this.isGroupMode) this.setGroupPaymentMethod(method)
    else this.setSelectedPaymentMethod(method)
  }

  setActiveNotes(value: string) {
    if (this.isGroupMode) this.setGroupNotes(value)
    else this.setBookingNotes(value)
  }

  clearSelectedServices() {
    this.selectedServices = []
  }

  clearDateTime() {
    this.selectedDate = ""
    this.selectedSlot = null
  }

  clearPayment() {
    this.selectedPaymentMethod = "Cash"
  }

  addService(item: CartServiceItem) {
    if (this.activeServices.some((s) => s.id === item.id)) return
    this.setActiveServices([...this.activeServices, item])
  }

  removeService(id: number) {
    this.setActiveServices(this.activeServices.filter((s) => s.id !== id))
  }

  resetGroup() {
    this.groupDate = ""
    this.groupMembers = []
    this.activeGroupMemberId = null
    this.groupPaymentMethod = "Cash"
    this.groupIsCustomBooking = false
    this.groupNotes = ""
    this.groupAvailability = null
  }

  reset() {
    this.selectedServices = []
    this.bookingMode = null
    this.currentStep = 1
    this.selectedDate = ""
    this.selectedSlot = null
    this.selectedPaymentMethod = "Cash"
    this.isLoading = false
    this.isCustomBooking = false
    this.acceptedTerms = false
    this.notificationsConsent = true
    this.bookingNotes = ""
    this.isBookingHydrating = false
    this.discountCode = ""
    this.resetGroup()
  }
}
